<template>
  <div class="home">
    <Modal
      v-if="demographicsModal.isOpen"
      dimmed
      :width="80"
      @close-modal="demographicsModal.isOpen = false"
    >
      <h2 v-translate>Complete your profile</h2>
      <SurveyResponseComponent
        :surveyResponse="demographicsModal.surveyResponse"
        :onlyDisplayQuestions="demographicsModal.questions"
        @survey-done="onSurveyDone"
      />
    </Modal>
    <Modal
      v-if="communicationsModal && !demographicsModal.isOpen"
      dimmed
      :width="80"
      @close-modal="communicationsModal = false"
    >
      <h2>How would you like us to communicate with you?</h2>
      <div class="comm-pref">
        <p>Fill out your communication preferences on the Profile page.</p>
        <router-link tag="button" class="primary-btn comm-pref__btn" :to="{ name: 'MyProfile' }"
          >Go to Profile</router-link
        >
      </div>
    </Modal>
    <!-- Organization Details -->
    <LogoHeader :org="organizationDetails" />
    <h4 class="sub-heading" v-if="!userIsLoggedIn" v-translate>
      We are all part of the same circle
    </h4>
    <div
      alt
      class="logo-wrapper-gc"
      v-if="!userIsLoggedIn"
      :style="{ backgroundImage: `url(${require('@/assets/images/logo-color.jpg')})` }"
    ></div>

    <!-- Join Now Section -->
    <div v-if="!userIsLoggedIn" class="join-now">
      <md-button @click="navigateToLogin" class="md-dense md-raised primary-btn">
        <span class="inherit-font" v-translate>Sign In</span>
      </md-button>
      <h5>
        <span v-translate class="inherit-font">New here?</span>
        <span>&nbsp;</span>
        <a class="link" @click="navigateToSignUp" v-translate>Join Now</a>
      </h5>
    </div>

    <!-- Home Page Copy Section -->
    <div v-if="!userIsLoggedIn" class="center">
      <span
        v-if="organizationDetails.homepageCopy"
        v-html="homeCopy"
      ></span>
      <p v-else-if="!organizationDetails.homepageCopy" v-translate>
        The Grant Circle is a pool of funds for and funded by the %{organizationDetails.name}
        community to support each other during financial emergencies.
      </p>
    </div>

    <div v-else class="center" style="margintop: 0">
      <h4 class="heading" v-translate="{ orgDetails: organizationDetails.name }">
        Welcome to the %{orgDetails} Grant Circle
      </h4>
      <p v-if="organizationDetails.donationsEnabled">
        <span class="spacing" v-translate
          >Use the links below, or in the menu, to give to the Grant Circle or request funds. Visit
          the</span
        >
        <span>&nbsp;</span>
        <router-link :to="{ name: 'MyActivity' }" v-translate>My Activity</router-link>
        <span>&nbsp;</span>
        <span class="spacing" v-translate
          >page to see your donation and request history, and to edit submitted or unfinished
          requests.</span
        >
      </p>
      <p v-if="!organizationDetails.donationsEnabled">
        <span class="spacing" v-translate
          >Use the links below, or in the menu, to request funds. Visit the&nbsp;</span
        >
        <span>&nbsp;</span>
        <router-link :to="{ name: 'MyActivity' }" v-translate>My Activity</router-link>
        <span>&nbsp;</span>
        <span class="spacing" v-translate
          >page to see your request history, and to edit submitted or unfinished requests.</span
        >
      </p>
    </div>
    <!-- End Home Page Copy Section -->

    <!-- Articles Section -->
    <!-- TODO - ELAN: Need a way to handle loading spinners but still be able to handle null stories -->
    <!-- <LoadSpinner v-if="!requestStory" /> -->
    <ArticleCard
      v-if="requestStory"
      :header="articleCard.requestStory.header"
      :subHeader="articleCard.requestStory.subHeader"
      :article="requestStory"
      :buttonText="articleCard.requestStory.buttonText"
      @click="onClickRequestGrantMoney"
    />

    <!-- NIP 9/30/20  Avoid having two stacked loading spinners by only rendering the second one if the first is gone -->
    <!-- <LoadSpinner v-if="!contributionStory && requestStory" /> -->
    <ArticleCard
      v-if="contributionStory && organizationDetails.donationsEnabled"
      :header="articleCard.contributionStory.header"
      :subHeader="articleCard.contributionStory.subHeader"
      :article="contributionStory"
      :buttonText="articleCard.contributionStory.buttonText"
      @click="onClickGive"
    />

    <ArticleCard
      v-if="organizationDetails.springFourEnabled"
      :header="articleCard.resources.header"
      :subHeader="articleCard.resources.subHeader"
      :buttonText="articleCard.resources.buttonText"
      @click="onClickFindResources"
    />
    <!-- End Articles Section -->
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ImpactStory from '@/services/impactStories'
import ArticleCard from '@/components/ArticleCard'
import LogoHeader from '@/components/LogoHeader'
import LoadSpinner from '@/components/LoadSpinner'
import Modal from '@/components/Modal'
import SurveyResponse from '@/services/surveyresponses'
import SurveyResponseComponent from '@/components/SurveyResponse'
import { getDefaultForCustomVariable } from '@/services/organizationCustomVariables'
import { analyticsMixin } from '@/services/googleAnalytics'
import { formatText } from '@/services/utils'

export default {
  name: 'Home',
  mixins: [analyticsMixin],
  components: {
    ArticleCard,
    LogoHeader,
    LoadSpinner,
    Modal,
    SurveyResponseComponent,
  },
  data() {
    return {
      requestStory: null,
      contributionStory: null,
      demographicsModal: {
        surveyName: 'Household Details',
        surveyResponse: null,
        questions: [96, 97], //Hardcoded demographic question HumanReadableIDs from CAN-570
        isOpen: false,
      },
      communicationsModal: false,
    }
  },
  async created() {
    // Load ImpactStories
    ImpactStory.api.getStories().then((stories) => {
      // 0th index should always be REQUEST_STORY, 1st index should always be CONTRIBUTION_STORY
      this.requestStory = stories[0]
      this.contributionStory = stories[1]
    })

    // If user is being routed here immediately post-login:
    const { fromLogin } = this.$route.params
    if (fromLogin) {
      // If user has missing demographic questions, show demographics modal
      if (await this.demographicQuestionsIncomplete()) {
        this.demographicsModal.isOpen = true
      }
      if (this.commPrefsNotSet()) {
        this.communicationsModal = true
      }
    }
  },
  methods: {
    getDefaultForCustomVariable,
    navigateToSignUp() {
      this.$router.push({ name: 'Verification' })
    },
    navigateToLogin() {
      this.$router.push({ name: 'Login' })
    },
    onSurveyDone() {
      this.demographicsModal.isOpen = false
    },
    onClickRequestGrantMoney() {
      if (this.userIsLoggedIn) {
        this.$router.push({ name: 'Request' })
      } else {
        this.$router.push({ name: 'Verification' })
      }
    },
    onClickGive() {
      this.$router.push({ name: 'AddPayment' })
    },
    onClickFindResources() {
      this.eventsTracker('clicked-cta-resources')
      this.$router.push({ name: 'FindResources' })
    },
    /* Return true if employee hasn't filled out one of the two new demographics questions
     * that are now part of the Household Details onboarding survey. (CAN-570) */
    async demographicQuestionsIncomplete() {
      if (
        !this.organizationDetails.organizationSurveys.some(
          (survey) => surveyName == this.demographicsModal.surveyName,
        )
      ) {
        return false
      }
      if (!this.user.isEmployee) {
        return false
      }
      const surveyResponse = await SurveyResponse.getOrCreate(
        this.user,
        this.demographicsModal.surveyName,
      )
      this.demographicsModal.surveyResponse = surveyResponse
      // If 1+ demographic questions are empty, return true
      return surveyResponse.questions.some((q) => {
        return this.demographicsModal.questions.includes(q.humanReadableId) && q.isEmpty
      })
    },
    commPrefsNotSet() {
      return this.user.isEmployee && !this.user.hasSetNotifPreferences
    },
  },
  computed: {
    homeCopy(){
      return formatText(this.$language.current,
            {
              'en_US': this.organizationDetails.homepageCopy.en,
              'es_ES': this.organizationDetails.homepageCopy.es,
            },
            {
              'org': this.organizationDetails,
            }
          )
    },
    articleCard() {
      const translated = this.$gettext(
        "Add money to the pool so it's there when you or your %{orgDetails} need it.",
      )

      return {
        requestStory: {
          header: this.$gettext('Request Grant Money'),
          subHeader: this.$gettext(
            'If an unexpected emergency has made it hard for you financially, request the money you need to get back on track.',
          ),
          buttonText: this.$gettext('Request'),
        },
        contributionStory: {
          header: this.$gettext('Give to the Grant Circle'),
          subHeader: this.$gettextInterpolate(translated, {
            orgDetails:
              (this.organizationDetails &&
                this.organizationDetails.customVariables &&
                this.$options.filters.translateToCurrentLang(
                  this.$language.current,
                  this.organizationDetails.customVariablesEs.cv2,
                  this.organizationDetails.customVariablesEn.cv2,
                )) ||
              getDefaultForCustomVariable('cv2', this.$language.current),
          }),
          buttonText: this.$gettext('Give'),
        },
        resources: {
          header: this.$gettext('Find money-saving resources'),
          subHeader: this.$gettext(
            'Connect with local, trusted resources designed to help you lower monthly costs and achieve financial stability.',
          ),
          buttonText: this.$gettext('Find Resources'),
        },
      }
    },
    ...mapState(['organizationDetails', 'user']),
    ...mapGetters(['userIsLoggedIn']),
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
.center {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1.5rem 0 3rem 0;
}
.heading {
  margin-top: 2rem;
}
.home {
  @include screen-container();
  padding: 2rem 2rem 3rem 2rem;
}
.sub-heading {
  text-align: center;
}
.join-now {
  display: flex;
  flex-direction: column;
  align-items: center;

  h5 {
    margin-bottom: 0;

    span {
      font-family: 'Poppins-Medium';
      font-weight: 500;
      font-size: 1rem;
      line-height: 1.5rem;
    }
  }
}
.logo-wrapper {
  height: 12rem;
  width: 90%;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-wrapper-gc {
  height: 4rem;
  width: 14rem;
  margin-bottom: 1rem;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}
.primary-btn {
  @include primary-button(var(--dark-brand-color));
  margin-bottom: 1.2rem;
}
.login-btn {
  color: var(--dark-brand-color);
  margin-top: 1rem;
  cursor: pointer;
}
.card-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.community-card {
  height: 290px;
  width: 100%;
  border-radius: 9px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  margin: 1rem;
  padding: 30px 15px;
  box-shadow: 0 4px 8px 0 $mid-gray;
  background-color: var(--light-brand-color);
}
.community-text {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.community-button {
  @include primary-button(var(--dark-brand-color));
  margin: 0 0 1rem 0;
}
.link {
  cursor: pointer;
}
.link:hover {
  text-decoration: underline;
}
.spacing {
  font-size: inherit;
  font-weight: inherit;
}
.comm-pref {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__btn {
    margin-top: 1em;
  }
}
</style>
